import { useSEO } from "@/hooks/useSEO";
import { Instagram, Youtube } from "lucide-react";
import profileImage from "@/assets/profile.jpg";
import { BRAND_HANDLE, BRAND_PERSON_NAME } from "@/lib/brandConfig";

const socialLinks = [
  { icon: Instagram, label: "Instagram", href: `https://instagram.com/${BRAND_HANDLE}` },
  { icon: Youtube, label: "YouTube", href: `https://www.youtube.com/@${BRAND_HANDLE}` },
];

const CreadoraContenido = () => {
  useSEO("creadoraContenido");

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-md mx-auto px-5 py-12 md:py-16">
        {/* Header / Profile Section */}
        <header className="text-center mb-10 animate-fade-in">
          <div className="relative inline-block mb-8">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-border via-muted to-border opacity-60" />
            <div className="relative w-28 h-28 rounded-full overflow-hidden ring-2 ring-background shadow-lg">
              <img
                src={profileImage}
                alt={`${BRAND_PERSON_NAME} - Digital content creator profile photo`}
                loading="lazy"
                width={112}
                height={112}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-background/10 to-transparent pointer-events-none" />
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-medium text-foreground tracking-wide mb-3 italic">
            {BRAND_PERSON_NAME}
          </h1>
          <div className="flex items-center justify-center gap-2 mb-5">
            <span className="w-6 h-px bg-border" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              Creadora de Contenido Digital
            </p>
            <span className="w-6 h-px bg-border" />
          </div>
        </header>

        {/* Content Section */}
        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4 text-center">
            <p className="text-sm leading-relaxed">
              Como <strong>creadora de contenido digital</strong>, mi pasión es compartir 
              contenido auténtico sobre lifestyle, belleza, bienestar y viajes. Creo conexiones 
              reales con mi comunidad a través de Instagram, TikTok y YouTube.
            </p>
            <p className="text-sm leading-relaxed">
              Mi contenido incluye rutinas de belleza, hábitos de bienestar y 
              experiencias de viaje, además de momentos cotidianos que inspiran a vivir con autenticidad.
            </p>
          </div>
        </section>

        {/* Social Links */}
        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <h2 className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground text-center mb-6 font-medium">
            Sígueme
          </h2>
          <div className="flex justify-center gap-6">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all duration-300"
                aria-label={link.label}
              >
                <link.icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </section>

        {/* Decorative Element */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <span className="w-12 h-px bg-border" />
          <span className="w-1 h-1 bg-muted-foreground/40 rotate-45" />
          <span className="w-12 h-px bg-border" />
        </div>

        {/* Topics Grid */}
        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.3s" }}>
          <h2 className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground text-center mb-6 font-medium">
            Temas
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {[
              { name: "Lifestyle", href: "/lifestyle" },
              { name: "Bienestar", href: "/nutricion" },
              { name: "Belleza", href: "/belleza" },
              { name: "Viajes", href: "/viajes" },
            ].map((topic) => (
              <a
                key={topic.name}
                href={topic.href}
                className="p-4 text-center border border-border rounded hover:border-foreground/30 transition-all duration-300"
              >
                <span className="text-xs tracking-wide text-muted-foreground hover:text-foreground">
                  {topic.name}
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* Back to Home */}
        <div className="text-center animate-fade-in" style={{ animationDelay: "0.4s" }}>
          <a
            href="/"
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Volver al inicio
          </a>
        </div>

        {/* Footer */}
        <footer className="mt-14 text-center animate-fade-in" style={{ animationDelay: "0.5s" }}>
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/60">
            {`@${BRAND_HANDLE}`}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default CreadoraContenido;

